// GameState types and factory. Everything in GameState must be JSON-serializable
// (no Map/Set/class instances) so save -> load is lossless.

import type { Command } from "./engine";
import { TUNABLES } from "@degen/shared/config/tunables";
import type { RngStream } from "./rng";
import { streamRng } from "./rng";
import type { MarketState } from "./market/market";
import { createMarket } from "./market/market";
import type { NpcTrader } from "./market/npcTraders";
import { createNpcs } from "./market/npcTraders";
import { createPlayer, type NewPlayerInput, type Player } from "./player";
import { createSocial, type SocialState } from "./social/feed";
import { createAirdrops, type AirdropState } from "./airdrops/protocols";
import { createNft, type NftState } from "./nft/drops";
import { createEvents, type EventsState } from "./events/sleuth";
import type { RektInfo } from "./rekt";
import { createPets, type PetsState } from "./pets/pets";
import { createCars, type CarsState } from "./cars/cars";
import { createHouses, grantStarterHome, type HousesState } from "./houses/houses";
import { createVano, type VanoState } from "./vano/vano";
import { createHackathons, type HackathonsState } from "./hackathons/hackathons";
import { createPerps, type PerpsState } from "./perps/perps";
import { createProgress, type ProgressState } from "./progress/progress";
import { createMessages, type MessagesState } from "./messages/messages";

export const SAVE_VERSION = 1;

export interface LogEntry {
  seq: number; // monotonically increasing, survives log trimming
  tick: number;
  kind: string;
  text: string;
  data?: Record<string, string | number>;
}

export interface RunRecord {
  input: NewPlayerInput;
  /** [tick, command] in dispatch order. Replaying them from the seed rebuilds this exact state. */
  actions: [number, Command][];
  /** Saves migrated from before run recording can't be replayed. */
  legacy?: boolean;
}

export interface GameState {
  version: typeof SAVE_VERSION;
  seed: number;
  tick: number;
  rng: Record<string, RngStream>;
  player: Player;
  market: MarketState;
  npcs: NpcTrader[];
  social: SocialState;
  airdrops: AirdropState;
  nft: NftState;
  events: EventsState;
  pets: PetsState;
  cars: CarsState;
  houses: HousesState;
  messages: MessagesState;
  vano: VanoState;
  hackathons: HackathonsState;
  perps: PerpsState;
  progress: ProgressState;
  /** Verified-run record (roadmap Phase 2): how this life started + every player command. */
  run?: RunRecord;
  /** Shared world (Phase 4): the market comes from the world server; trades go through it. */
  shared?: { worldId: string; userId: string };
  log: LogEntry[];
  logSeq: number;
  rekt: RektInfo | null;
}

export function createGameState(seed: number, input: NewPlayerInput): GameState {
  const rng: Record<string, RngStream> = {};
  const state: GameState = {
    version: SAVE_VERSION,
    seed: seed >>> 0,
    tick: 0,
    rng,
    player: createPlayer(input),
    market: createMarket(),
    npcs: [],
    social: undefined as unknown as SocialState,
    airdrops: createAirdrops(),
    nft: createNft(),
    events: createEvents(),
    pets: createPets(),
    cars: createCars(),
    houses: createHouses(),
    messages: createMessages(),
    vano: createVano(),
    hackathons: createHackathons(),
    perps: createPerps(),
    progress: createProgress(),
    log: [],
    logSeq: 0,
    rekt: null,
  };
  state.npcs = createNpcs(streamRng(rng, state.seed, "npcs-init"));
  state.social = createSocial(streamRng(rng, state.seed, "social-init"));
  grantStarterHome(state, input.origin === "kol");
  return state;
}

export function pushLog(state: GameState, kind: string, text: string, data?: LogEntry["data"]) {
  const seq = state.logSeq++;
  state.log.push(data ? { seq, tick: state.tick, kind, text, data } : { seq, tick: state.tick, kind, text });
  if (state.log.length > TUNABLES.save.logCap) state.log.splice(0, state.log.length - TUNABLES.save.logCap);
}

export const ticksPerDay = () => TUNABLES.clock.ticksPerDay;
export const dayOf = (tick: number) => Math.floor(tick / TUNABLES.clock.ticksPerDay) + 1;
export const minuteOfDay = (tick: number) => tick % TUNABLES.clock.ticksPerDay;

export function formatClock(tick: number): string {
  const m = minuteOfDay(tick);
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}
